import React, { useEffect, useState } from "react"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import ProgressBar from "aldoo-ra/ProgressBar"
import Modal from "aldoo-ra/Modal"
import UpdateListDialog from "./update-list-dialog"
import getRealtimeDeploymentController from "./realtime-deployment-controller"
import AppDetailsDialog from "./app-details-dialog"
import StatusChips from "./status-chips"

const controller = getRealtimeDeploymentController()

const AppCard = ({ item, statusColorMap }) => {
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false)
  const [updateStatus, setUpdateStatus] = useState(null)
  const [showUpdateList, setShowUpdateList] = useState(false)
  const [unknownStateCount, setUnknownStateCount] = useState(0)
  const [wasRecentlyDeploying, setWasRecentlyDeploying] = useState(false)
  const [deploymentPhase, setDeploymentPhase] = useState(null) // Track deployment lifecycle
  const [, forceUpdate] = useState(0) // Force re-render hook
  const [loadingStartTime, setLoadingStartTime] = useState(null) // Track when loading started
  const [deploymentStartTime, setDeploymentStartTime] = useState(null) // Track deployment start for client-side timer
  const [showErrorModal, setShowErrorModal] = useState(false)
  const [copied, setCopied] = useState(false)
  const [logFilters, setLogFilters] = useState({ error: true, warn: false, info: false })
  const [isRescanning, setIsRescanning] = useState(false)
  const [clientElapsedTime, setClientElapsedTime] = useState(0) // Client-side elapsed time calculation

  // Clear local update status when external status indicates deployment is complete
  useEffect(() => {
    if (item.deploymentState?.status === "completed" && updateStatus) {
      // Clear any errors/status when deployment completes successfully
      setUpdateStatus(null)
      // Reset deployment phase when completed
      setDeploymentPhase("awaiting_final_status")
    }
    
    // If deployment status changes from running to idle, clear the task
    if (item.deploymentState?.status === "idle" && item.updateTask) {
      item.updateTask = null;
      // Reset deployment phase
      setDeploymentPhase(null)
    }
  }, [item.deploymentState?.status, updateStatus])

  // Track deployment state changes and unknown state counting
  useEffect(() => {
    const currentStatus = item.deploymentState?.status;
    const gitStatus = item.gitState?.status;

    // Track deployment phase transitions
    if (currentStatus === "running" && deploymentPhase !== "deploying") {
      setDeploymentPhase("deploying")
      setWasRecentlyDeploying(true);
      setUnknownStateCount(0); // Reset unknown count during active deployment
      setLoadingStartTime(null); // Reset loading timer during active deployment

      // Sync client timer to server timer when deployment actually starts
      if (!deploymentStartTime && item.deploymentState?.elapsed) {
        // Calculate when server started based on current elapsed time
        const serverStartTime = Date.now() - item.deploymentState.elapsed;
        setDeploymentStartTime(serverStartTime);
      } else if (!deploymentStartTime) {
        // Fallback if no server elapsed time yet
        setDeploymentStartTime(Date.now());
      }
    } else if (currentStatus === "completed" && gitStatus === "updated" && deploymentPhase !== null) {
      // Deployment completed and git shows updated - clear everything immediately
      setDeploymentPhase(null)
      setWasRecentlyDeploying(false);
      setLoadingStartTime(null);
      setDeploymentStartTime(null);
      setClientElapsedTime(0);
    } else if (currentStatus === "completed" && gitStatus === "pending" && deploymentPhase !== null) {
      // Deployment completed but git still shows pending (new updates available) - clear phase
      setDeploymentPhase(null)
      setWasRecentlyDeploying(false);
      setLoadingStartTime(null);
      setDeploymentStartTime(null);
      setClientElapsedTime(0);
    } else if (gitStatus === "pending" && deploymentPhase === null && !wasRecentlyDeploying) {
      // New updates detected after deployment cycle completed - allow normal pending display
      setLoadingStartTime(null); // Clear loading timer for new updates
    }

    // Count unknown states only if we have some deployment data or were recently deploying
    const hasDeploymentData = item.deploymentState || item.gitState;
    const shouldTrackUnknown = hasDeploymentData && wasRecentlyDeploying;

    if (shouldTrackUnknown && (!currentStatus || currentStatus === "unknown") && (!gitStatus || gitStatus === "unknown")) {
      setUnknownStateCount(prev => prev + 1);
    } else if (currentStatus && currentStatus !== "unknown" && gitStatus && gitStatus !== "unknown") {
      // Reset count when we get valid states
      setUnknownStateCount(0);
      if (currentStatus === "completed" || gitStatus === "updated") {
        // With deploymentPhase state machine, we can clear immediately
        setWasRecentlyDeploying(false);
      }
    }
  }, [item.deploymentState?.status, item.gitState?.status])

  // Sync client timer with server updates
  useEffect(() => {
    const serverElapsed = item.deploymentState?.elapsed;
    const isActiveDeployment = item.deploymentState?.status === "running";

    if (isActiveDeployment && serverElapsed && serverElapsed > 0 && deploymentStartTime) {
      // "Correct" client timer by adjusting start time based on server elapsed
      const correctedStartTime = Date.now() - serverElapsed;

      // Only adjust if there's a significant difference (> 1 second)
      const currentClientElapsed = Date.now() - deploymentStartTime;
      if (Math.abs(currentClientElapsed - serverElapsed) > 1000) {
        setDeploymentStartTime(correctedStartTime);
      }
    }
  }, [item.deploymentState?.elapsed, item.deploymentState?.status, deploymentStartTime])

  // Continuous timer effect for client-side elapsed time calculation
  useEffect(() => {
    let interval;
    const isDeploying = item.deploymentState?.status === "running" ||
                       (item.deploymentState?.progress > 0 && item.deploymentState?.progress < 100);

    if (deploymentStartTime && isDeploying) {
      interval = setInterval(() => {
        const elapsed = Date.now() - deploymentStartTime;
        setClientElapsedTime(elapsed);
      }, 1000); // Update every second
    } else if (!isDeploying) {
      setClientElapsedTime(0);
    }

    return () => {
      if (interval) clearInterval(interval);
    };
  }, [deploymentStartTime, item.deploymentState?.status, item.deploymentState?.progress])

  // Real-time updates are handled by admin-site-update-scan.jsx through Socket.io
  // No more polling needed - deployment progress comes through real-time events

  const handleRunUpdate = async () => {
    try {
      setShowUpdateList(false)
      
      // Reset unknown state tracking when starting new deployment
      setUnknownStateCount(0)
      setWasRecentlyDeploying(true)
      setDeploymentPhase("starting") // Set initial phase
      // Client timer will sync when actual deployment starts
      
      // Immediately show "starting..." when user clicks update
      setUpdateStatus({
        status: "starting",
        activity: "Starting update...",
        progress: 0
      })
      
      // Also update deployment state immediately for instant visual feedback
      if (!item.deploymentState) {
        item.deploymentState = {}
      }
      item.deploymentState.status = "running"
      item.deploymentState.progress = 0
      item.deploymentState.currentStep = "Starting deployment..."
      item.deploymentState.elapsed = 0 // Initialize elapsed time
      
      // Force re-render to show progress bar immediately
      forceUpdate(prev => prev + 1)

      // Use Socket.io to start deployment
      const response = await controller.runAppUpdate(item.id)

      if (!response?.success) {
        throw new Error(response?.error || "Failed to start deployment")
      }

      // Store task ID if provided
      if (response.data?.task_id) {
        item.updateTask = response.data.task_id
      }
    } catch (error) {
      setUpdateStatus({
        error: error.message,
        status: "error",
      })
      item.updateTask = null
    }
  }

  const handleRescan = async () => {
    try {
      setIsRescanning(true)
      setUpdateStatus(null)
      setDeploymentPhase(null)
      await controller.resetAppError(item.id)
      // State reset + scan broadcast will come through the realtime channel,
      // which clears the failed state and updates gitState on this card automatically
    } catch (error) {
      console.error('Rescan failed:', error.message)
    } finally {
      setIsRescanning(false)
    }
  }

  // Get internal status (single word)
  const getInternalStatus = () => {
    
    // PRIORITY 1: Check for failure first to override any local starting state
    if (item.deploymentState?.status === "failed") return "failed"

    // PRIORITY 2: Check deployment state - if running, show deploying regardless of git state
    if (item.deploymentState?.status === "running") {
      return "deploying"
    }

    // Handle local update states (user clicked update but deployment hasn't started yet)
    if (updateStatus?.status === "starting") return "starting"
    if (updateStatus?.status === "running") return "updating"
    if (updateStatus?.status === "error") return "failed"
    
    // Handle deployment failure
    if (item.deploymentState?.status === "failed") {
      return "failed"
    }
    
    // Handle deployment completed state
    if (item.deploymentState?.status === "completed") {
      // Git state is set to "updated" in the same broadcast as completion,
      // so we can trust it immediately without any loading delay
      if (item.gitState?.status === "pending") {
        return "pending"
      }
      return "updated"
    }
    
    // Handle special deployment states that indicate ongoing activity
    // If we have deployment progress/steps, show as deploying even if status is temporarily "idle" (race condition fix)
    if (item.deploymentState?.progress > 0 && item.deploymentState?.progress < 100) {
      return "deploying"
    }
    
    if (item.deploymentState?.currentStep && item.deploymentState.currentStep.toLowerCase().includes("restarting")) {
      return "deploying"
    }
    
    // PRIORITY 2: If deployment is idle or not present, use git state
    if (item.gitState?.status === "pending") {
      return "pending"
    }
    
    if (item.gitState?.status === "updated") {
      return "updated"
    }
    
    // Fallback for unknown/undefined states
    return item.status || "unknown"
  }

  const timeAgo = (dateStr) => {
    if (!dateStr) return null
    const d = new Date(dateStr)
    if (isNaN(d.getTime())) return null
    const seconds = Math.floor((Date.now() - d.getTime()) / 1000)
    if (seconds < 60) return "just now"
    const minutes = Math.floor(seconds / 60)
    if (minutes < 60) return `${minutes}m ago`
    const hours = Math.floor(minutes / 60)
    if (hours < 24) return `${hours}h ago`
    const days = Math.floor(hours / 24)
    if (days < 30) return `${days}d ago`
    const months = Math.floor(days / 30)
    if (months < 12) return `${months}mo ago`
    return `${Math.floor(months / 12)}y ago`
  }

  // Status display mapping for localization
  // TODO: Move this to a centralized localization module
  const getStatusDisplayText = (status) => {
    const localDate = item.gitState?.localRevision?.date
    const statusDisplayMap = {
      updated: localDate ? `Updated ${timeAgo(localDate)}` : "up to date",
      pending: "updates available",
      deploying: "deploying...",
      starting: "starting...",
      updating: "updating...",
      running: "running...",
      created: "created",
      completed: "completed",
      failed: "error",
      unknown: "unknown",
      loading: "refreshing..."
    }
    return statusDisplayMap[status] || status
  }

  const internalStatus = getInternalStatus()
  const displayStatus = getStatusDisplayText(internalStatus)


  // Format elapsed time in MM:SS format
  const formatElapsedTime = (elapsed) => {
    if (!elapsed || elapsed <= 0) return null;
    const minutes = Math.floor(elapsed / 60000);
    const seconds = Math.floor((elapsed % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
  

  const isUpdating =
    Boolean(item.updateTask) || 
    updateStatus?.status === "starting" ||
    updateStatus?.status === "running" ||
    item.deploymentState?.status === "running"

  return (
    <>
      <div className="flex items-center gap-4 p-2">
        <div className="flex items-center gap-2">
          {updateStatus?.status === "running" ? (
            <svg
              className="w-5 h-5 text-primary animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          ) : (
            <div
              className={`w-3 h-3 rounded-full ${
                statusColorMap[internalStatus] || statusColorMap["unknown"]
              }`}
            />
          )}
          <div className="flex flex-col gap-2 min-w-[200px] py-6 ml-5">
            <div className="flex items-baseline gap-2 flex-wrap">
              <Typography
                variant="admin_h4"
                className="hover:text-admin_primary font-semibold cursor-pointer leading-none"
                onClick={() => setIsDetailsModalOpen(true)}
              >
                {item.name}
              </Typography>
              <Typography
                variant="admin_body_dim"
                className="mb-0 leading-none"
              >
                {displayStatus}
              </Typography>
            </div>

            <div className="mt-2 mb-1">
              <StatusChips status={item.status} />
            </div>
          </div>
        </div>

        {internalStatus !== "failed" && (updateStatus?.status === "starting" || updateStatus?.status === "running" || item.deploymentState?.status === "running" || internalStatus === "loading" || internalStatus === "deploying") ? (
          <div className="flex flex-col flex-grow">
            <div className="flex flex-grow">
              {(() => {
                // During deployment, ONLY use deployment state - ignore command progress
                const isDeploymentActive = item.deploymentState?.status === "running" || internalStatus === "deploying";
                const isLoading = internalStatus === "loading";

                if (isDeploymentActive) {
                  // Deployment is active - use only deployment state
                  const progress = item.deploymentState.progress || 0;
                  const currentStep = item.deploymentState.currentStep;

                  // Avoid showing "Deploying..." with 0% - use actual step or show initializing
                  let title = currentStep || (progress > 0 ? "Deploying..." : "Initializing...");

                  return (
                    <ProgressBar
                      title={title}
                      variant="default"
                      progress={progress / 100}
                    />
                  );
                } else if (isLoading) {
                  // Show loading state with indeterminate progress
                  return (
                    <ProgressBar
                      title="loading..."
                      variant="default"
                      progress={0}
                    />
                  );
                } else {
                  // No active deployment - use legacy command progress
                  const progress = updateStatus?.progress || 0;
                  const title = updateStatus?.activity || "Updating...";
                  return (
                    <ProgressBar
                      title={title}
                      variant="default"
                      progress={progress / 100}
                    />
                  );
                }
              })()}
            </div>
            {/* Always show elapsed time below progress bar when deployment is active */}
            {(item.deploymentState?.status === "running" || internalStatus === "deploying") && (
              <div className="flex justify-center mt-2">
                <Typography
                  variant="admin_body_dim"
                  className="text-xs text-gray-500"
                >
                  Elapsed: {(() => {
                    // Primarily use client timer for continuous updates, but sync to server when updates arrive
                    const serverElapsed = item.deploymentState?.elapsed;

                    // Use client timer if it's running, fallback to server timer
                    const displayElapsed = (clientElapsedTime > 0) ? clientElapsedTime : (serverElapsed || 0);

                    return formatElapsedTime(displayElapsed) || "0:00";
                  })()}
                </Typography>
              </div>
            )}
          </div>
        ) : (
          (() => {
            if (internalStatus === "failed") {
              return (
                <div className="flex justify-end flex-grow gap-2">
                  <Button
                    variant="admin_secondary"
                    className="border-admin_error text-admin_error hover:bg-admin_error/10"
                    onClick={() => setShowErrorModal(true)}
                  >
                    Error
                  </Button>
                  <Button
                    variant="admin_secondary"
                    disabled={isRescanning}
                    onClick={handleRescan}
                  >
                    {isRescanning ? "Scanning..." : "Rescan"}
                  </Button>
                </div>
              )
            }

            const hasUpdates = (item.updates?.length > 0 || item.gitState?.updates?.length > 0);
            const notLoading = internalStatus !== "loading";
            const notDeploying = internalStatus !== "deploying";
            const notActivelyDeploying = item.deploymentState?.status !== "running";
            const notAwaitingFinalStatus = deploymentPhase !== "awaiting_final_status";

            return hasUpdates && notLoading && notDeploying && notActivelyDeploying && notAwaitingFinalStatus && (
              <div className="flex justify-end flex-grow">
                <Button
                  variant="admin_secondary"
                  onClick={() => setShowUpdateList(true)}
                >
                  {`${(item.updates?.length || item.gitState?.updates?.length || 0)} update${
                    (item.updates?.length || item.gitState?.updates?.length || 0) > 1 ? "s" : ""
                  }`}
                </Button>
              </div>
            );
          })()
        )}
      </div>

      <AppDetailsDialog
        isOpen={isDetailsModalOpen}
        onClose={() => setIsDetailsModalOpen(false)}
        item={item}
      />

      <UpdateListDialog
        isOpen={showUpdateList}
        onClose={() => setShowUpdateList(false)}
        updates={item.updates || item.gitState?.updates || []}
        onUpdate={handleRunUpdate}
        isUpdating={isUpdating}
      />

      <Modal
        isOpen={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        variant="admin_compact_fixed"
      >
        <div className="p-4 space-y-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Typography variant="admin_h4" className="text-admin_error">
              Deployment Error
            </Typography>
            <button
              onClick={() => {
                navigator.clipboard.writeText(updateStatus?.error || item.deploymentState?.error || "")
                setCopied(true)
                setTimeout(() => setCopied(false), 2000)
              }}
              className={`transition-colors ${copied ? "text-admin_success" : "text-admin_text_secondary hover:text-admin_primary"}`}
              title="Copy to clipboard"
            >
              {copied ? (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                </svg>
              ) : (
                <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
                </svg>
              )}
            </button>
            <div className="flex items-center gap-1 ml-2">
              {[
                { key: "error", label: "Error", activeColor: "#7f1d1d", activeText: "#fff" },
                { key: "warn",  label: "Warn",  activeColor: "#92400e", activeText: "#fbbf24" },
                { key: "info",  label: "Info",  activeColor: "#374151", activeText: "#9ca3af" },
              ].map(({ key, label, activeColor, activeText }) => (
                <button
                  key={key}
                  onClick={() => setLogFilters(prev => ({ ...prev, [key]: !prev[key] }))}
                  style={
                    logFilters[key]
                      ? { background: activeColor, color: activeText, border: `1px solid ${activeText}33` }
                      : { background: "transparent", color: "#4b5563", border: "1px solid #374151" }
                  }
                  className="text-xs px-2 py-0.5 rounded transition-colors"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div
            className="overflow-y-auto rounded-lg p-3 font-mono text-xs"
            style={{ background: "#141414", minHeight: "500px", maxHeight: "60vh" }}
          >
            {(() => {
              // First pass: assign mode to every line
              let mode = "normal"
              const lines = (updateStatus?.error || item.deploymentState?.error || "No error details available")
                .split("\n")
                .map((line) => {
                  if (/\[Warning\]/i.test(line)) mode = "warn"
                  else if (/^(Error|error)/.test(line)) mode = "error"
                  else if (/^(warn|Warn|WARNING|warning)/.test(line)) mode = "warn"
                  else if (/Script execution failed/i.test(line)) mode = "error"
                  const lineMode = mode
                  if (line.trimEnd().endsWith(".")) mode = "normal"
                  return { line, lineMode }
                })

              // Second pass: filter based on active toggles
              return lines
                .filter(({ lineMode }) => {
                  if (lineMode === "error") return logFilters.error
                  if (lineMode === "warn") return logFilters.warn
                  return logFilters.info
                })
                .map(({ line, lineMode }, i) => (
                  <div
                    key={i}
                    style={
                      lineMode === "error"
                        ? { background: "#7f1d1d", color: "#fff", padding: "1px 4px", borderRadius: 2 }
                        : lineMode === "warn"
                        ? { color: "#fbbf24", padding: "1px 4px" }
                        : { color: "#4b5563", padding: "1px 4px" }
                    }
                  >
                    {line || "\u00a0"}
                  </div>
                ))
            })()}
          </div>
        </div>
      </Modal>
    </>
  )
}

export default AppCard
